import { useState, useEffect } from 'react';
import { db } from './firestore';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import logger from '../utils/logger';
import { USE_CONTRACTS_V2 } from './contractsV2';
import { finesService, officeError } from './finesService';

const SETTINGS_DOC = doc(db, 'settings', 'company');
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

// Current hard-coded values across the app, kept as defaults so nothing
// changes in behavior until an admin edits them from the UI.
export const DEFAULT_COMPANY_SETTINGS = {
    fuel_price_per_liter: 2.78,
    price_per_km: 1.0,
    trn: '100397403500003',
    // "To" recipients of the day-13 collections e-mail (Reminder -> Collections).
    reminder_recipients: {
        dubai: ['dubaiair@iyelo.com', 'dubaiair2@iyelo.com'],
    },
    // The fine fee values are NOT here (audit DUP-15): invygo_service_fee_rate / invygo_service_tax_rate /
    // non_rta_surcharge_aed were editable in Administration but read by nothing - the engine's fines config
    // owns them (FINES_SERVICE_FEE_PCT, FINES_FEE_VAT_PCT, FINES_IMPOUND_KNOWLEDGE_FEE, portal_knowledge_fee)
    // and the web mirrors the fee rate for its invoices in constants/finance.js (FINE_SERVICE_FEE_RATE).
    // `trn` above is the one TRN every tax invoice prints.
};

let _cache = null; // { data, fetchedAt }

// The settings document: Firestore settings/company, or - with the new store (USE_CONTRACTS_V2) - the engine's
// office records (GET /api/office/settings/company answers {key, data, version, ...}; {} when never saved).
const readRemote = async () => {
    if (USE_CONTRACTS_V2) return (await finesService.officeSettings('company'))?.data || {};
    const snap = await getDoc(SETTINGS_DOC);
    return snap.exists() ? snap.data() : {};
};

/**
 * Reads settings/company merged over DEFAULT_COMPANY_SETTINGS, caching the
 * result in module memory for CACHE_DURATION. Never throws — returns the
 * defaults (or the last good cache) if the settings cannot be read.
 */
export const getCompanySettings = async () => {
    const now = Date.now();
    if (_cache && (now - _cache.fetchedAt) < CACHE_DURATION) {
        return _cache.data;
    }

    try {
        const remote = await readRemote();
        const merged = { ...DEFAULT_COMPANY_SETTINGS, ...remote };
        _cache = { data: merged, fetchedAt: now };
        return merged;
    } catch (error) {
        logger.error('Error fetching company settings, falling back to defaults:', error);
        // Keep serving a stale cache if we have one, otherwise the defaults.
        return _cache ? _cache.data : DEFAULT_COMPANY_SETTINGS;
    }
};

/**
 * Merges `partial` into settings/company (admin-only, per firestore.rules - and the engine's PUT: admin only).
 * Invalidates the in-memory cache on success so the next read is fresh. The store stamps last_updated itself.
 */
export const saveCompanySettings = async (partial) => {
    try {
        if (USE_CONTRACTS_V2) {
            await finesService.officeSettingsSave('company', { ...partial }).catch((e) => { throw officeError(e); });
        } else {
            await setDoc(SETTINGS_DOC, {
                ...partial,
                last_updated: new Date().toISOString(),
            }, { merge: true });
        }
        _cache = null;
        return { success: true };
    } catch (error) {
        logger.error('Error saving company settings:', error);
        return { success: false, error };
    }
};

/**
 * React hook: returns defaults immediately, then swaps in the settings
 * document once it resolves (or keeps defaults on error).
 */
export const useCompanySettings = () => {
    const [settings, setSettings] = useState(DEFAULT_COMPANY_SETTINGS);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        getCompanySettings().then((data) => {
            if (!cancelled) {
                setSettings(data);
                setLoading(false);
            }
        });
        return () => { cancelled = true; };
    }, []);

    return { settings, loading };
};
